import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HttpBackend, HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import * as mammoth from 'mammoth/mammoth.browser';

export interface PdfViewerData {
  key?: string;
  url?: string;
  blob?: Blob;
  fileName?: string;
  mimeType?: string;
}

@Component({
  selector: 'app-pdf-view',
  templateUrl: './pdf-view.component.html',
  styleUrls: ['./pdf-view.component.scss']
})
export class PdfViewComponent implements OnInit, OnDestroy {
  pdfSrc = '';
  isPdf = true;
  isDoc = false;
  isDocx = false;
  isLegacyDoc = false;
  isLoading = true;
  errorMsg = '';
  docHtml = '';
  fileSrc: SafeResourceUrl | null = null;

  private documentKey = '';
  private fileName = 'document';
  private sourceBlob: Blob | null = null;
  private objectUrl: string | null = null;
  private loadSubscription: Subscription | null = null;
  private externalHttp: HttpClient;

  constructor(
    public dialogRef: MatDialogRef<PdfViewComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PdfViewerData | Blob | ArrayBuffer | Uint8Array | string | number,
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    httpBackend: HttpBackend
  ) {
    this.externalHttp = new HttpClient(httpBackend);
  }

  ngOnInit(): void {
    const source = this.resolveSource(this.data);
    if (source.blob) {
      this.sourceBlob = source.blob;
      this.fileName = source.fileName || 'document';
      this.setFileType(this.fileName, source.mimeType || source.blob.type);
      this.detectFileTypeFromBlob(source.blob);
      if (this.isDoc) {
        this.renderDocPreview(source.blob);
      } else {
        this.setObjectUrl(source.blob);
        this.isLoading = false;
      }
      return;
    }

    if (source.url) {
      this.fileName = source.fileName || this.fileNameFromKey(source.url);
      this.setFileType(this.fileName);
      this.loadRemoteDocument(source.url);
      return;
    }

    const key = String(source.key || '').trim();
    if (!key) {
      this.isLoading = false;
      this.errorMsg = 'No document was provided.';
      return;
    }

    this.documentKey = key;
    this.fileName = source.fileName || this.fileNameFromKey(key);
    this.setFileType(this.fileName);
    const url = `master/document?key=${encodeURIComponent(key)}&disposition=inline`;
    this.loadSubscription = this.http.get(url, this.getBlobRequestOptions())
      .subscribe({
        next: async (response: HttpResponse<Blob>) => {
          const blob = response.body as Blob;
          if (!blob) {
            this.isLoading = false;
            this.errorMsg = 'Unable to load document';
            return;
          }
          this.sourceBlob = blob;
          this.detectFileTypeFromBlob(blob);
          if (this.isDoc) {
            await this.renderDocPreview(blob);
            return;
          }
          this.setObjectUrl(blob);
          this.isLoading = false;
        },
        error: (_err) => {
          this.isLoading = false;
          this.errorMsg = 'Unable to load document';
        }
      });
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.revokeObjectUrl();
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  download(): void {
    if (this.sourceBlob) {
      this.downloadBlob(this.sourceBlob, this.fileName);
      return;
    }
    if (!this.documentKey) {
      return;
    }

    this.http.get(
      `master/document?key=${encodeURIComponent(this.documentKey)}&disposition=attachment`,
      this.getDownloadBlobRequestOptions()
    ).subscribe({
      next: (blob: Blob) => this.downloadBlob(blob, this.fileName),
      error: () => this.errorMsg = 'Unable to download document'
    });
  }

  private loadRemoteDocument(url: string): void {
    this.loadSubscription = this.externalHttp.get(url, this.getBlobRequestOptions(false))
      .subscribe({
        next: async (response: HttpResponse<Blob>) => {
          const blob = response.body as Blob;
          if (!blob) {
            this.isLoading = false;
            this.errorMsg = 'Unable to load document';
            return;
          }
          this.sourceBlob = blob;
          this.detectFileTypeFromBlob(blob);
          if (this.isDoc) {
            await this.renderDocPreview(blob);
            return;
          }
          this.setObjectUrl(blob);
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.errorMsg = 'Unable to load document';
        }
      });
  }

  private resolveSource(data: PdfViewerData | Blob | ArrayBuffer | Uint8Array | string | number): {
    key?: string;
    url?: string;
    blob?: Blob;
    fileName?: string;
    mimeType?: string;
  } {
    if (data instanceof Blob) {
      return { blob: data, fileName: this.fileNameFromType(data.type), mimeType: data.type };
    }
    if (data instanceof ArrayBuffer) {
      return { blob: new Blob([data]), fileName: 'document.pdf', mimeType: 'application/pdf' };
    }
    if (ArrayBuffer.isView(data)) {
      const bytes = new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
      return {
        blob: new Blob([bytes]),
        fileName: 'document.pdf',
        mimeType: 'application/pdf'
      };
    }
    if (typeof data === 'string' || typeof data === 'number') {
      return { key: String(data).trim() };
    }
    if (data && typeof data === 'object') {
      return {
        key: data.key,
        url: data.url,
        blob: data.blob instanceof Blob ? data.blob : undefined,
        fileName: data.fileName,
        mimeType: data.mimeType
      };
    }
    return {};
  }

  private setFileType(fileName: string, mimeType = ''): void {
    const normalizedName = String(fileName || '').toLowerCase();
    const normalizedMime = String(mimeType || '').toLowerCase();
    this.isPdf = normalizedName.endsWith('.pdf') || normalizedMime.includes('application/pdf');
    this.isDocx = normalizedName.endsWith('.docx') ||
      normalizedMime.includes('application/vnd.openxmlformats-officedocument.wordprocessingml.document');
    this.isLegacyDoc = normalizedName.endsWith('.doc') || normalizedMime.includes('application/msword');
    this.isDoc = this.isDocx || this.isLegacyDoc;
  }

  private detectFileTypeFromBlob(blob: Blob): void {
    const mime = (blob?.type || '').toLowerCase();
    if (!mime || mime === 'application/octet-stream') {
      return;
    }
    if (mime.includes('application/pdf')) {
      this.isPdf = true;
      this.isDoc = false;
      this.isDocx = false;
      this.isLegacyDoc = false;
      return;
    }
    if (mime.includes('application/vnd.openxmlformats-officedocument.wordprocessingml.document')) {
      this.isPdf = false;
      this.isDoc = true;
      this.isDocx = true;
      this.isLegacyDoc = false;
      return;
    }
    if (mime.includes('application/msword')) {
      this.isPdf = false;
      this.isDoc = true;
      this.isDocx = false;
      this.isLegacyDoc = true;
      return;
    }
    if (mime.startsWith('image/')) {
      this.isPdf = false;
      this.isDoc = false;
      this.isDocx = false;
      this.isLegacyDoc = false;
    }
  }

  private async renderDocPreview(blob: Blob): Promise<void> {
    try {
      const arrayBuffer = await this.readBlobAsArrayBuffer(blob);
      const result = await mammoth.convertToHtml({ arrayBuffer });
      this.docHtml = result.value || '<p>No preview content available.</p>';
    } catch (_error) {
      this.setObjectUrl(blob);
    } finally {
      this.isLoading = false;
    }
  }

  private setObjectUrl(blob: Blob): void {
    this.revokeObjectUrl();
    this.objectUrl = URL.createObjectURL(blob);
    this.pdfSrc = this.objectUrl;
    this.fileSrc = this.sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl);
  }

  private revokeObjectUrl(): void {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }

  private readBlobAsArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = () => reject(new Error('Failed to read document'));
      reader.readAsArrayBuffer(blob);
    });
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'document';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 100);
  }

  private fileNameFromKey(key: string): string {
    try {
      const cleanKey = decodeURIComponent(key.split('?')[0]);
      return cleanKey.split('/').pop() || 'document';
    } catch (_error) {
      return key.split('/').pop() || 'document';
    }
  }

  private fileNameFromType(mimeType: string): string {
    const normalized = (mimeType || '').toLowerCase();
    if (normalized.includes('pdf')) {
      return 'document.pdf';
    }
    if (normalized.includes('wordprocessingml')) {
      return 'document.docx';
    }
    if (normalized.includes('msword')) {
      return 'document.doc';
    }
    return 'document';
  }

  private getBlobRequestOptions(includeAuth = true) {
    return {
      headers: includeAuth ? this.getAuthHeaders() : new HttpHeaders(),
      responseType: 'blob' as const,
      observe: 'response' as const
    };
  }

  private getDownloadBlobRequestOptions() {
    return {
      headers: this.getAuthHeaders(),
      responseType: 'blob' as const
    };
  }

  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('userToken');
    let headers = new HttpHeaders({ Device: 'Desktop' });
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }
}
